import { type ToolSpec, n, s, fmt, money } from "./types";

const GIFT_COINS: Record<string, number> = {
  universe: 34999,
  lion: 29999,
  galaxy: 1000,
  rose: 1,
  drama_queen: 5000,
  interstellar: 10000,
  custom: 0,
};

const CURRENCY_RATE: Record<string, number> = {
  USD: 1,
  PKR: 278,
  INR: 87,
  GBP: 0.79,
  EUR: 0.92,
};

const CURRENCY_SYM: Record<string, string> = {
  USD: "$",
  PKR: "₨",
  INR: "₹",
  GBP: "£",
  EUR: "€",
};

const LLM_PARAMS: Record<string, number> = {
  "7B": 7,
  "8B": 8,
  "13B": 13,
  "34B": 34,
  "70B": 70,
};

// Approximate architecture constants scaled by parameter count (billions)
const llmArchConstants = (paramsB: number) => {
  // hidden dim ~ scales with sqrt of params, layers scale roughly linearly
  const layers = Math.round(6.5 * Math.cbrt(paramsB) + 8);
  const hiddenDim = Math.round((paramsB * 1e9) / (12 * layers * layers) ** 0.5 / 1000) * 1000 || 4096;
  const kvHeads = Math.max(8, Math.round(hiddenDim / 128));
  const headDim = 128;
  return { layers, hiddenDim, kvHeads, headDim };
};

const QUANT_BITS: Record<string, number> = {
  fp16: 16,
  q8_0: 8.5,
  q5_k_m: 5.5,
  q4_k_m: 4.5,
  q2_k: 2.6,
};

const GPU_TIERS = [8, 12, 16, 24, 48, 80];
const recommendGpu = (vram: number): string => {
  const tier = GPU_TIERS.find((t) => t >= vram);
  return tier ? `${tier}GB GPU` : "Multi-GPU (>80GB)";
};

const RES_BITRATE: Record<string, number> = {
  "1080p": 4,
  "2K": 6,
  "4K": 12,
};

const COMPRESSION_FACTOR: Record<string, number> = {
  h264: 1.0,
  h265: 0.5,
  "h265+": 0.35,
};

const HDD_SIZES = [1, 2, 4, 6, 8, 10, 12, 16];
const recommendHdd = (tb: number): string => {
  const size = HDD_SIZES.find((h) => h >= tb);
  return size ? `${size}TB HDD` : `${Math.ceil(tb)}TB (RAID / NAS array)`;
};

export const CREATOR_TOOLS: ToolSpec[] = [
  {
    slug: "tiktok-live-gift-calculator",
    cluster: "digital",
    name: "TikTok Live Gift to USD / Local Payout Calculator",
    h1: "TikTok Live Gift to Cash Calculator: Diamonds to USD, PKR & INR",
    title: "TikTok Gift to Cash Calculator | Free Calculator (2026)",
    description:
      "Calculate net creator payout instantly. Free online TikTok live gift to cash calculator with live formulas, unit conversion and a step-by-step breakdown.",
    keywords: [
      "tiktok gift to usd calculator",
      "tiktok diamonds to cash converter",
      "how much is a universe gift worth on tiktok",
      "tiktok live gift payout pkr inr",
      "tiktok coins to diamonds calculator",
      "tiktok creator fund gift split calculator",
    ],
    inputs: [
      {
        id: "gift",
        label: "Gift type",
        type: "select",
        def: "universe",
        options: [
          { value: "universe", label: "Universe (34,999 coins)" },
          { value: "lion", label: "Lion (29,999 coins)" },
          { value: "interstellar", label: "Interstellar (10,000 coins)" },
          { value: "drama_queen", label: "Drama Queen (5,000 coins)" },
          { value: "galaxy", label: "Galaxy (1,000 coins)" },
          { value: "rose", label: "Rose (1 coin)" },
          { value: "custom", label: "Custom coins" },
        ],
      },
      { id: "coins", label: "Number of coins (used if custom, or gift quantity)", type: "number", def: "1", unit: "gifts", min: 1, max: 100000, step: 1 },
      { id: "customCoins", label: "Custom coin value per gift (if custom selected)", type: "number", def: "1000", unit: "coins", min: 1, max: 34999, step: 1 },
      {
        id: "currency",
        label: "Payout currency",
        type: "select",
        def: "USD",
        options: [
          { value: "USD", label: "USD ($)" },
          { value: "PKR", label: "PKR (₨)" },
          { value: "INR", label: "INR (₹)" },
          { value: "GBP", label: "GBP (£)" },
          { value: "EUR", label: "EUR (€)" },
        ],
      },
      { id: "agencyCut", label: "Agency / MCN cut", type: "number", def: "0", unit: "%", min: 0, max: 70, step: 1 },
    ],
    compute: (v) => {
      const giftKey = s(v, "gift", "universe");
      const quantity = Math.max(0, n(v, "coins", 1));
      const coinsPerGift = giftKey === "custom" ? Math.max(0, n(v, "customCoins", 1000)) : GIFT_COINS[giftKey] ?? 0;
      const totalCoins = coinsPerGift * quantity;
      const diamonds = totalCoins * 0.5;
      const grossUsd = diamonds * 0.005;
      const agencyPct = Math.min(70, Math.max(0, n(v, "agencyCut", 0))) / 100;
      const currency = s(v, "currency", "USD");
      const rate = CURRENCY_RATE[currency] ?? 1;
      const sym = CURRENCY_SYM[currency] ?? "$";
      const tiktokCutUsd = grossUsd * 0.5;
      const creatorGrossUsd = grossUsd * 0.5;
      const netUsd = creatorGrossUsd * (1 - agencyPct);
      const netLocal = netUsd * rate;
      return {
        cards: [
          { label: "Total diamonds", value: fmt(diamonds), sub: `${fmt(totalCoins)} coins spent`, accent: true },
          { label: "Gross USD value", value: money(grossUsd, "$", 2), sub: "Diamonds × $0.005" },
          { label: "TikTok's 50% cut", value: money(tiktokCutUsd, "$", 2), sub: "Platform revenue share" },
          { label: "Net creator payout", value: money(netLocal, sym, 2), sub: `After ${fmt(agencyPct * 100, 0)}% agency cut, in ${currency}` },
        ],
        table: {
          title: "Payout breakdown",
          head: ["Metric", "Value"],
          rows: [
            ["Coins per gift", fmt(coinsPerGift)],
            ["Quantity", fmt(quantity)],
            ["Total coins", fmt(totalCoins)],
            ["Total diamonds", fmt(diamonds)],
            ["Gross USD", money(grossUsd, "$", 2)],
            ["Creator gross share (50%)", money(creatorGrossUsd, "$", 2)],
            ["Agency cut", money(creatorGrossUsd * agencyPct, "$", 2)],
            [`Net payout (${currency})`, money(netLocal, sym, 2)],
          ],
        },
        summary: `${fmt(quantity)} × ${giftKey === "custom" ? "custom gift" : giftKey} generates ${fmt(diamonds)} diamonds, worth ${money(netLocal, sym, 2)} net to the creator after platform and agency cuts.`,
      };
    },
    intro:
      "TikTok LIVE gifts convert real money into coins, coins into diamonds, and diamonds into a creator payout that is split roughly in half between TikTok and the streamer. This calculator reverse-engineers that pipeline so you can see exactly what a Universe, Lion or custom gift is worth in USD, PKR or INR after typical agency cuts.",
    sections: [
      { h: "How TikTok gifting actually works", body: "Viewers buy coins with real money, then spend coins on animated gifts during a LIVE stream. Each gift converts to diamonds at a fixed 2:1 coin-to-diamond ratio, and diamonds are TikTok's internal accounting unit for creator earnings. Creators cannot withdraw diamonds directly — they must be redeemed for cash through TikTok's payout system, which is where the 50% platform cut is applied." },
      { h: "Why payouts vary by country", body: "TikTok does not publish a universal payout rate, and real redemption rates fluctuate by region and account tier. The $0.005 per diamond figure used here is a widely cited indicative rate; established creators with agency backing sometimes negotiate better terms. Treat the output as a planning estimate, not a guaranteed bank deposit." },
      { h: "Where agency and MCN cuts fit in", body: "Many creators work with an agency or multi-channel network on a talent contract, which typically takes an additional cut of gross diamond earnings for management, moderation, or exclusivity — commonly 10% to 40%. Enter that percentage to see your realistic take-home instead of the gross platform-level figure." },
      { h: "Reading the local currency conversion", body: "Currency conversion uses fixed indicative FX rates for quick reference; real bank transfers will differ slightly with each payout cycle's actual rate and any transfer fees. For creators earning in PKR or INR, this gap matters most on larger gift totals like Universe or Lion trains." },
    ],
    staticTable: {
      title: "Common TikTok gift values",
      head: ["Gift", "Coins", "Diamonds", "Gross USD", "Creator gross (50%)"],
      rows: [
        ["Rose", "1", "0.5", "$0.005", "$0.0025"],
        ["Galaxy", "1,000", "500", "$2.50", "$1.25"],
        ["Drama Queen", "5,000", "2,500", "$12.50", "$6.25"],
        ["Interstellar", "10,000", "5,000", "$25.00", "$12.50"],
        ["Lion", "29,999", "14,999.5", "$74.998", "$37.50"],
        ["Universe", "34,999", "17,499.5", "$87.498", "$43.75"],
      ],
    },
    faqs: [
      { q: "How much is one TikTok diamond worth?", a: "Diamonds are generally estimated at about $0.005 each, though TikTok does not publish an official fixed rate and it can vary by region and payout batch." },
      { q: "Do creators get 100% of gift value?", a: "No. TikTok typically retains roughly half of the diamond value, meaning the creator's gross share before any agency cut is around 50% of the gift's dollar value." },
      { q: "What is the coin-to-diamond conversion ratio?", a: "Gifts are priced in coins, and coins convert to diamonds at a 2:1 ratio, so 1,000 coins spent on a gift generates 500 diamonds toward the creator's balance." },
      { q: "How much is a Universe gift worth to the creator?", a: "A Universe gift costs 34,999 coins, producing about 17,499.5 diamonds and roughly $87.50 gross value, of which the creator's estimated share before agency cuts is about $43.75." },
      { q: "Why is my actual TikTok payout different from this estimate?", a: "TikTok's actual redemption rate, regional payout policies, and any agency contract terms can shift the real number; this tool uses a commonly cited indicative rate for planning purposes." },
      { q: "What percentage do agencies usually take from gift earnings?", a: "MCN and agency cuts vary widely, commonly landing between 10% and 40% of gross diamond earnings, depending on the level of support and exclusivity in the contract." },
      { q: "Can I calculate custom or rare gift values?", a: "Yes, select 'Custom coins' and enter the exact coin price of any gift not listed to get the same diamond and payout breakdown." },
      { q: "Does TikTok pay creators directly in diamonds?", a: "No, diamonds are an internal accounting unit; creators must redeem their diamond balance for real currency through TikTok's payout or PayPal system, subject to minimum thresholds." },
      { q: "How accurate is the PKR/INR conversion?", a: "The tool uses fixed indicative FX rates for quick reference; actual bank conversion rates and transfer fees will cause small day-to-day differences." },
      { q: "Is gifting income taxable for creators?", a: "In most jurisdictions, yes — LIVE gifting payouts are typically treated as self-employment or miscellaneous income and should be reported, though rules vary by country so check local tax guidance." },
    ],
    formula: {
      expressions: [
        "Diamonds = Coins × 0.5",
        "Gross_USD = Diamonds × 0.005",
        "Net = Gross_USD × 0.5 × (1 − Agency%) × FX_Rate",
      ],
      variables: [
        { name: "Coins", meaning: "Total coins spent on the gift(s)" },
        { name: "Diamonds", meaning: "Creator-side diamond balance generated" },
        { name: "Agency%", meaning: "Percentage cut taken by agency/MCN" },
        { name: "FX_Rate", meaning: "Indicative currency conversion rate from USD" },
      ],
    },
    related: ["substack-patreon-fee-calculator", "llm-gpu-vram-calculator", "css-clamp-calculator", "cctv-storage-calculator"],
  },

  {
    slug: "css-clamp-calculator",
    cluster: "digital",
    name: "CSS Clamp Calculator",
    h1: "CSS Clamp Calculator: Instant Fluid Typography & Responsive Layouts",
    title: "CSS Clamp() Fluid Typography Calculator | Free Calculator (2026)",
    description:
      "Calculate fluid font sizing instantly. Free online CSS clamp calculator with live formulas, unit conversion and a step-by-step breakdown.",
    keywords: [
      "css clamp calculator fluid typography",
      "generate clamp function for responsive font size",
      "css clamp min max vw calculator",
      "fluid font size formula generator",
      "responsive rem font size calculator",
    ],
    inputs: [
      { id: "minWidth", label: "Min screen width", type: "number", def: "375", unit: "px", min: 200, max: 1200, step: 1 },
      { id: "maxWidth", label: "Max screen width", type: "number", def: "1440", unit: "px", min: 400, max: 3000, step: 1 },
      { id: "minFont", label: "Min font size", type: "number", def: "16", unit: "px", min: 8, max: 96, step: 0.5 },
      { id: "maxFont", label: "Max font size", type: "number", def: "48", unit: "px", min: 8, max: 200, step: 0.5 },
      { id: "rootSize", label: "Root font size", type: "number", def: "16", unit: "px", min: 10, max: 24, step: 1 },
    ],
    compute: (v) => {
      const minW = Math.max(1, n(v, "minWidth", 375));
      const maxW = Math.max(minW + 1, n(v, "maxWidth", 1440));
      const minF = Math.max(0.1, n(v, "minFont", 16));
      const maxF = Math.max(minF, n(v, "maxFont", 48));
      const root = Math.max(1, n(v, "rootSize", 16));

      const slope = (maxF - minF) / (maxW - minW);
      const intercept = -minW * slope + minF;
      const slopeVw = slope * 100;
      const minRem = minF / root;
      const maxRem = maxF / root;
      const interceptRem = intercept / root;

      const clampStr = `clamp(${fmt(minRem, 4).replace(/,/g, "")}rem, ${fmt(interceptRem, 4).replace(/,/g, "")}rem + ${fmt(slopeVw, 4).replace(/,/g, "")}vw, ${fmt(maxRem, 4).replace(/,/g, "")}rem)`;

      const sizeAt = (w: number) => {
        const px = Math.min(maxF, Math.max(minF, intercept + slope * w));
        return px;
      };

      return {
        cards: [
          { label: "clamp() value", value: clampStr, sub: "font-size", accent: true },
          { label: "Slope", value: `${fmt(slopeVw, 4)}vw`, sub: "Growth rate per viewport width" },
          { label: "Intercept", value: `${fmt(interceptRem, 4)}rem`, sub: "Base offset at 0px viewport" },
          { label: "Range", value: `${fmt(minF, 1)}px – ${fmt(maxF, 1)}px`, sub: `${fmt(minRem, 3)}rem – ${fmt(maxRem, 3)}rem` },
        ],
        table: {
          title: "Font size at sample breakpoints",
          head: ["Viewport width", "Font size (px)", "Font size (rem)"],
          rows: [
            [`${fmt(minW)}px (min)`, fmt(sizeAt(minW), 2), fmt(sizeAt(minW) / root, 3)],
            [`${fmt((minW + maxW) / 2)}px (mid)`, fmt(sizeAt((minW + maxW) / 2), 2), fmt(sizeAt((minW + maxW) / 2) / root, 3)],
            [`${fmt(maxW)}px (max)`, fmt(sizeAt(maxW), 2), fmt(sizeAt(maxW) / root, 3)],
          ],
        },
        summary: `Copy: font-size: ${clampStr}; scales smoothly from ${fmt(minF, 1)}px at ${fmt(minW)}px viewport to ${fmt(maxF, 1)}px at ${fmt(maxW)}px viewport.`,
      };
    },
    intro:
      "The CSS clamp() function lets a value scale fluidly between a minimum and maximum, using a viewport-relative unit as the middle argument, so you avoid dozens of media-query breakpoints. This calculator does the algebra for you and outputs a ready-to-paste clamp() string for font-size, padding or any length property.",
    sections: [
      { h: "How the fluid slope is derived", body: "Fluid typography treats font size as a straight line between two points: (minWidth, minFont) and (maxWidth, maxFont). The slope of that line, expressed as a percentage of viewport width, becomes the vw component; the y-intercept becomes the rem offset. Below minWidth and above maxWidth, clamp() locks the value to the min or max bound instead of extrapolating." },
      { h: "Why rem instead of px in the output", body: "Using rem units for the bounds respects a user's browser font-size preference for accessibility, while the vw component still responds to viewport changes. Mixing rem and vw in one calc() expression, wrapped in clamp(), is the standard modern approach recommended by most CSS specifications and browser vendors." },
      { h: "Common use beyond typography", body: "The same clamp() math works for padding, margin, gap, and even border-radius — anywhere you want smooth, breakpoint-free scaling. Just plug your target min/max pixel values and viewport range into this tool regardless of which CSS property you're styling." },
      { h: "Browser support and fallbacks", body: "clamp() has been supported in all major browsers since 2020, so a fallback is rarely necessary today. If you must support very old browsers, pair the clamp() rule with a plain px fallback declared immediately before it, since CSS applies the last valid rule it understands." },
    ],
    staticTable: {
      title: "Typical fluid type scale references",
      head: ["Use case", "Min font (px)", "Max font (px)", "Typical viewport range"],
      rows: [
        ["Body text", "16", "18", "375px – 1440px"],
        ["Small caption", "12", "14", "375px – 1440px"],
        ["H3 heading", "20", "28", "375px – 1440px"],
        ["H2 heading", "28", "40", "375px – 1440px"],
        ["H1 heading", "32", "56", "375px – 1440px"],
        ["Hero display", "40", "96", "375px – 1920px"],
      ],
    },
    faqs: [
      { q: "What does the CSS clamp() function do?", a: "clamp(min, preferred, max) picks the preferred value unless it falls outside the min/max bounds, in which case it locks to whichever bound was exceeded, giving smooth fluid scaling with hard limits." },
      { q: "Why use vw units inside clamp() instead of just percentages?", a: "vw is relative to the viewport width regardless of the element's parent container, giving predictable scaling tied directly to screen size, which is what fluid typography needs." },
      { q: "Can I use clamp() for spacing, not just font-size?", a: "Yes, clamp() works for any CSS length property including padding, margin, width, and gap; the calculator's slope and intercept math applies identically." },
      { q: "What happens below my minimum screen width?", a: "The clamp() value freezes at the minimum bound; the font will not shrink further even on very narrow screens, preventing illegibly small text." },
      { q: "How precise should the vw/rem decimals be?", a: "Four decimal places is generally sufficient for pixel-accurate rendering; browsers round sub-pixel values anyway, so extra precision rarely changes the visual result." },
      { q: "Does clamp() replace media queries entirely?", a: "For simple linear scaling, yes, but complex layout changes like reflowing a grid still need media queries; clamp() is best suited to continuous properties like size and spacing." },
      { q: "Why is my clamp() output showing the max value on desktop-sized screens?", a: "If your max viewport width is set too low, screens larger than that will always compute a preferred value above the max and get clamped, which is expected behavior." },
      { q: "Is clamp() supported in all browsers?", a: "Yes, all major browsers (Chrome, Firefox, Safari, Edge) have supported clamp() since 2020, so it's safe to use in production without fallbacks in most projects." },
      { q: "How do I convert rem back to px?", a: "Multiply the rem value by your root font size, typically 16px unless the user or site has changed it; this calculator's table shows both units directly." },
      { q: "What is a sensible min/max width range for fluid type?", a: "375px (small mobile) to 1440px (standard desktop) covers the vast majority of real-world viewports and is the most commonly used range in production design systems." },
    ],
    formula: {
      expressions: [
        "slope = (maxFont − minFont) / (maxWidth − minWidth)",
        "intercept = −minWidth × slope + minFont",
        "clamp(minRem, interceptRem + slope×100vw, maxRem)",
      ],
      variables: [
        { name: "slope", meaning: "Rate of font growth per pixel of viewport width" },
        { name: "intercept", meaning: "Base font value extrapolated to a 0px viewport" },
        { name: "minRem/maxRem", meaning: "Min/max font size converted to rem units" },
      ],
    },
    related: ["llm-gpu-vram-calculator", "tiktok-live-gift-calculator", "cctv-storage-calculator", "podcast-mic-gain-calculator"],
  },

  {
    slug: "podcast-mic-gain-calculator",
    cluster: "digital",
    name: "Podcast Mic Gain & Distance Calculator",
    h1: "Podcast Mic Gain & Distance Calculator: Ideal Audio Levels & Noise Floor",
    title: "Podcast Mic Gain Calculator | Free Calculator (2026)",
    description:
      "Calculate required preamp gain instantly. Free online podcast mic gain calculator with live formulas, unit conversion and a step-by-step breakdown.",
    keywords: [
      "podcast mic gain calculator",
      "how much gain do I need for my microphone",
      "microphone distance and gain relationship",
      "audio interface gain staging calculator",
      "dynamic vs condenser mic gain needed",
    ],
    inputs: [
      {
        id: "micType",
        label: "Microphone type",
        type: "select",
        def: "dynamic",
        options: [
          { value: "dynamic", label: "Dynamic (e.g. SM7B, RE20)" },
          { value: "condenser", label: "Condenser (e.g. NT1, AT2020)" },
        ],
      },
      { id: "sensitivity", label: "Mic sensitivity", type: "number", def: "-59", unit: "dBV/Pa", min: -70, max: -20, step: 0.5 },
      { id: "distance", label: "Mouth-to-mic distance", type: "number", def: "6", unit: "inches", min: 1, max: 36, step: 0.5 },
      { id: "maxGain", label: "Interface max clean gain", type: "number", def: "60", unit: "dB", min: 20, max: 76, step: 1 },
    ],
    compute: (v) => {
      const micType = s(v, "micType", "dynamic");
      const sensitivity = n(v, "sensitivity", -59);
      const distance = Math.max(0.5, n(v, "distance", 6));
      const maxGain = Math.max(1, n(v, "maxGain", 60));

      const distanceLoss = 20 * Math.log10(distance / 2);
      const requiredGain = Math.abs(sensitivity) + 18 + distanceLoss;
      const boosterNeeded = requiredGain > maxGain;
      const headroom = maxGain - requiredGain;
      const snrEstimate = micType === "dynamic" ? 100 - requiredGain * 0.3 : 110 - requiredGain * 0.3;
      const proximityWarning = micType === "dynamic" && distance < 3;

      return {
        cards: [
          { label: "Required preamp gain", value: `${fmt(requiredGain, 1)}dB`, sub: `At ${fmt(distance, 1)}" distance`, accent: true },
          { label: "Booster needed", value: boosterNeeded ? "Yes" : "No", sub: boosterNeeded ? "Interface can't provide clean gain" : `${fmt(Math.max(0, headroom), 1)}dB headroom` },
          { label: "Estimated SNR", value: `${fmt(Math.max(0, snrEstimate), 0)}dB`, sub: "Higher is cleaner" },
          { label: "Proximity effect", value: proximityWarning ? "Likely boomy bass" : "Minimal", sub: micType === "dynamic" ? "Dynamic mics bass-boost when close" : "Condensers less affected" },
        ],
        table: {
          title: "Gain requirement breakdown",
          head: ["Factor", "Value"],
          rows: [
            ["Mic type", micType === "dynamic" ? "Dynamic" : "Condenser"],
            ["Sensitivity (abs)", `${fmt(Math.abs(sensitivity), 1)}dB`],
            ["Distance loss (20·log10(d/2))", `${fmt(distanceLoss, 2)}dB`],
            ["Baseline offset", "18dB"],
            ["Required gain", `${fmt(requiredGain, 1)}dB`],
            ["Interface max gain", `${fmt(maxGain, 1)}dB`],
            ["Headroom vs max", `${fmt(headroom, 1)}dB`],
          ],
        },
        summary: boosterNeeded
          ? `Your interface's ${fmt(maxGain, 0)}dB max gain falls short of the ${fmt(requiredGain, 1)}dB needed — add a cloudlifter or in-line booster, or move closer to the mic.`
          : `Your interface has enough headroom (${fmt(headroom, 1)}dB spare) to drive this mic cleanly at ${fmt(distance, 1)}" without an external booster.`,
      };
    },
    intro:
      "Getting a clean podcast recording starts with correct gain staging: too little and your voice sits in the noise floor, too much and cheap preamps add audible hiss. This calculator estimates the preamp gain your microphone actually needs based on its sensitivity and your mouth-to-mic distance, and flags when you need a hardware booster.",
    sections: [
      { h: "Why distance matters so much", body: "Sound pressure level falls off with the inverse square law, so doubling your distance from the mic costs roughly 6dB of level, which the preamp must then make up with extra gain. Moving from 2 inches to 6 inches away can mean a 10dB swing in required gain — often the difference between a quiet USB interface coping fine or falling short." },
      { h: "Dynamic vs condenser gain needs", body: "Dynamic mics like the Shure SM7B have low output sensitivity and typically need 55-65dB of clean gain at normal podcasting distance, which is why they're famous for needing a Cloudlifter or FetHead with budget interfaces. Condenser mics have built-in preamps and much hotter output, usually needing 20-35dB less gain for the same result." },
      { h: "What the SNR estimate tells you", body: "Signal-to-noise ratio approximates how much your voice sits above the interface's self-noise floor once gain is applied. Pushing a weak mic signal through very high gain amplifies the preamp's own noise floor right alongside your voice, which is exactly the scenario an in-line booster is designed to avoid by adding clean gain before the interface." },
      { h: "Managing the proximity effect", body: "Directional dynamic mics exaggerate bass response as the sound source gets closer, producing a warm, boomy tone prized in radio and podcasting but easy to overdo. If your mix sounds muddy at close range, either back off an inch or two or apply a gentle high-pass filter around 80-100Hz in post." },
    ],
    staticTable: {
      title: "Typical gain needs by mic and distance",
      head: ["Mic", "Sensitivity", "Distance", "Typical gain needed"],
      rows: [
        ["Shure SM7B", "-59dBV/Pa", "2 in", "60-66dB"],
        ["Shure SM7B", "-59dBV/Pa", "6 in", "66-72dB"],
        ["Electro-Voice RE20", "-56dBV/Pa", "4 in", "55-62dB"],
        ["Audio-Technica AT2020", "-37dBV/Pa", "6 in", "35-42dB"],
        ["Rode NT1", "-31.9dBV/Pa", "8 in", "28-35dB"],
        ["Shure MV7 (XLR)", "-55dBV/Pa", "4 in", "50-58dB"],
      ],
    },
    faqs: [
      { q: "Why does my SM7B sound so quiet even at max gain?", a: "The SM7B has very low sensitivity and typically needs 60-70dB of clean gain; many budget audio interfaces max out around 50-55dB, which is exactly why an in-line booster like a Cloudlifter or FetHead is so commonly paired with it." },
      { q: "What is a cloudlifter and do I need one?", a: "It's a passive-powered in-line booster that adds roughly 20-25dB of clean gain before your signal reaches the interface preamp, letting low-output dynamic mics reach proper levels without cranking a noisy preamp to its limit." },
      { q: "How close should I talk to my podcast mic?", a: "Most podcasting mics sound best at 4 to 8 inches, balancing full body and warmth against off-axis noise and excessive proximity bass buildup; closer than 2 inches usually sounds boomy and unnatural." },
      { q: "What is a good target recording level for podcasting?", a: "Aim for peaks around -12dB to -6dB with average (RMS) levels near -18dB to -20dB, leaving enough headroom to avoid clipping while staying well above the noise floor." },
      { q: "Why does distance affect gain by 6dB per doubling?", a: "This follows the inverse square law for sound propagation: sound pressure level drops by about 6dB every time the distance from the source doubles in a free field." },
      { q: "Do condenser mics ever need a booster?", a: "Rarely — condensers have built-in active preamps and high output level, so they almost always provide enough signal for standard interfaces without needing extra in-line gain." },
      { q: "What causes hiss when I turn my gain up high?", a: "Every preamp has an inherent self-noise floor; pushing gain higher to compensate for a weak signal amplifies that noise floor proportionally, which is audible as hiss on quiet passages." },
      { q: "Is 18dB a fixed constant in every gain calculation?", a: "It's used here as a practical baseline offset representing typical mic preamp calibration and target level headroom; real-world results can shift a few dB depending on interface design and desired loudness." },
      { q: "What is the proximity effect exactly?", a: "It's the bass boost that occurs in directional (cardioid) microphones as the sound source moves very close to the capsule, caused by the physics of pressure-gradient mic design." },
      { q: "Should I use a dynamic or condenser mic for podcasting?", a: "Dynamic mics reject more room noise and bleed, making them better for untreated rooms, while condensers capture more detail and need less gain but also pick up more ambient sound — choose based on your room treatment." },
    ],
    formula: {
      expressions: [
        "Distance_Loss = 20 × log10(distance / 2)",
        "Required_Gain = |sensitivity| + 18 + Distance_Loss",
        "Booster needed if Required_Gain > interface max gain",
      ],
      variables: [
        { name: "sensitivity", meaning: "Mic output sensitivity in dBV/Pa (negative value)" },
        { name: "distance", meaning: "Mouth-to-mic distance in inches" },
        { name: "Distance_Loss", meaning: "Additional gain required due to distance beyond the 2-inch reference" },
      ],
    },
    related: ["css-clamp-calculator", "cctv-storage-calculator", "llm-gpu-vram-calculator", "tiktok-live-gift-calculator"],
  },

  {
    slug: "substack-patreon-fee-calculator",
    cluster: "finance",
    name: "Substack vs Patreon Creator Net Revenue Calculator",
    h1: "Substack vs Patreon Creator Net Revenue & Fee Split Calculator",
    title: "Substack vs Patreon Fee Calculator | Free Calculator (2026)",
    description:
      "Calculate net creator take-home instantly. Free online Substack vs Patreon fee calculator with live formulas, unit conversion and a step-by-step breakdown.",
    keywords: [
      "substack vs patreon fees calculator",
      "how much does substack take from subscriptions",
      "patreon fee calculator net income",
      "creator subscription revenue after fees",
      "substack stripe fee breakdown calculator",
    ],
    inputs: [
      {
        id: "platform",
        label: "Platform / tier",
        type: "select",
        def: "substack",
        options: [
          { value: "substack", label: "Substack (10%)" },
          { value: "patreon_pro", label: "Patreon Pro (8%)" },
          { value: "patreon_premium", label: "Patreon Premium (12%)" },
        ],
      },
      { id: "subs", label: "Number of paid subscribers", type: "number", def: "500", unit: "subs", min: 0, max: 1000000, step: 1 },
      { id: "price", label: "Tier price", type: "number", def: "5", unit: "$/month", min: 0.5, max: 500, step: 0.5 },
      { id: "iosShare", label: "Share of subs paying via iOS in-app purchase", type: "number", def: "10", unit: "%", min: 0, max: 100, step: 1 },
    ],
    compute: (v) => {
      const platform = s(v, "platform", "substack");
      const platformFeePct = platform === "substack" ? 0.10 : platform === "patreon_pro" ? 0.08 : 0.12;
      const subs = Math.max(0, n(v, "subs", 500));
      const price = Math.max(0, n(v, "price", 5));
      const iosPct = Math.min(100, Math.max(0, n(v, "iosShare", 10))) / 100;

      const gross = subs * price;
      const platformFee = gross * platformFeePct;
      const stripeFee = gross * 0.029 + subs * 0.30;
      const appleFee = gross * iosPct * 0.30;
      const net = gross - platformFee - stripeFee - appleFee;
      const effectiveFeePct = gross > 0 ? ((gross - net) / gross) * 100 : 0;

      const platformLabel = platform === "substack" ? "Substack" : platform === "patreon_pro" ? "Patreon Pro" : "Patreon Premium";

      return {
        cards: [
          { label: "Gross monthly revenue", value: money(gross, "$", 2), sub: `${fmt(subs)} subs × $${fmt(price, 2)}`, accent: true },
          { label: "Platform fee", value: money(platformFee, "$", 2), sub: `${platformLabel} (${fmt(platformFeePct * 100, 0)}%)` },
          { label: "Net take-home", value: money(net, "$", 2), sub: `${fmt(effectiveFeePct, 1)}% total fees` },
          { label: "Effective fee rate", value: `${fmt(effectiveFeePct, 1)}%`, sub: "Combined platform + payment fees" },
        ],
        table: {
          title: "Fee breakdown",
          head: ["Fee component", "Amount"],
          rows: [
            ["Gross revenue", money(gross, "$", 2)],
            [`${platformLabel} platform fee`, money(platformFee, "$", 2)],
            ["Stripe processing fee (2.9% + $0.30/sub)", money(stripeFee, "$", 2)],
            [`Apple in-app fee (${fmt(iosPct * 100, 0)}% of subs × 30%)`, money(appleFee, "$", 2)],
            ["Total fees", money(gross - net, "$", 2)],
            ["Net take-home", money(net, "$", 2)],
          ],
        },
        summary: `On ${platformLabel}, ${fmt(subs)} subscribers at $${fmt(price, 2)}/month net ${money(net, "$", 2)} after fees — an effective fee rate of ${fmt(effectiveFeePct, 1)}%.`,
      };
    },
    intro:
      "Substack and Patreon both take a cut of creator subscription revenue, but the real cost is higher once you add Stripe payment processing and Apple's in-app purchase tax for mobile subscribers. This calculator lays out every fee layer side by side so you can see your true take-home, not just the headline platform percentage.",
    sections: [
      { h: "The headline fee isn't the whole story", body: "Substack advertises 10% and Patreon's standard plans range from 8% to 12%, but neither figure includes payment processing. Stripe charges roughly 2.9% plus $0.30 per transaction on top, which disproportionately hurts creators with many low-price subscribers versus fewer high-price ones." },
      { h: "The hidden Apple tax on mobile subscribers", body: "When a subscriber signs up through an iOS app rather than the web, Apple's App Store rules force platforms to route that transaction through Apple's in-app purchase system, taking an additional 30% cut (15% for smaller developers under Apple's reduced program). This is why many creator platforms actively steer mobile users toward web checkout links instead." },
      { h: "Why per-subscriber fixed fees matter at scale", body: "The $0.30 flat Stripe fee per subscriber means a $3/month tier loses 10% of revenue to that fee alone, while a $20/month tier loses just 1.5%. If you're pricing low to attract volume, run the numbers — sometimes fewer higher-priced subscribers net more cash even with lower headline conversion." },
      { h: "Choosing between Substack and Patreon", body: "Substack's flat 10% is simple and includes built-in newsletter distribution and audience discovery tools, while Patreon's tiered 8-12% pricing depends on which feature bundle you select, plus optional merch and community tools. The right choice usually depends more on your content format and audience habits than the fee percentage alone." },
    ],
    staticTable: {
      title: "Fee rates by platform and processor",
      head: ["Platform", "Platform fee", "Payment processing", "Apple IAP (if applicable)"],
      rows: [
        ["Substack", "10%", "2.9% + $0.30/sub", "30% of iOS-routed revenue"],
        ["Patreon Lite", "5%", "2.9% + $0.30/sub", "30% of iOS-routed revenue"],
        ["Patreon Pro", "8%", "2.9% + $0.30/sub", "30% of iOS-routed revenue"],
        ["Patreon Premium", "12%", "2.9% + $0.30/sub", "30% of iOS-routed revenue"],
        ["Ko-fi (membership)", "0-5%", "2.9% + $0.30/sub", "N/A (web only)"],
      ],
    },
    faqs: [
      { q: "How much does Substack actually take from subscriptions?", a: "Substack charges a flat 10% platform fee, but Stripe's payment processing (about 2.9% plus $0.30 per subscriber) is charged on top, so your real cost is closer to 12-15% depending on your price point." },
      { q: "Is Patreon cheaper than Substack?", a: "It depends on the tier: Patreon Lite at 5% can be cheaper, but Patreon Pro (8%) and Premium (12%) sit close to or above Substack's flat 10%, and all plans still add Stripe processing fees on top." },
      { q: "Why do I lose 30% on some subscribers but not others?", a: "That happens specifically to subscribers who signed up through an iOS mobile app rather than a web browser, because Apple requires in-app purchases to go through its payment system and takes a 30% cut." },
      { q: "How can I avoid the Apple in-app purchase fee?", a: "Direct your audience to sign up via a web browser link rather than through an iOS app; most creators share their subscription page URL directly on social media to sidestep the IAP tax entirely." },
      { q: "Does the $0.30 Stripe fee apply per subscriber or per transaction?", a: "It applies per transaction, which for monthly subscriptions effectively means per subscriber per billing cycle, making it hit hardest on high-volume, low-price subscription tiers." },
      { q: "What's a realistic effective fee rate for most creators?", a: "Most creators without significant iOS in-app traffic see an effective fee rate between 12% and 16% once platform fees and Stripe processing are combined." },
      { q: "Should I price my subscription higher to reduce fee impact?", a: "Yes, because the flat $0.30 per-subscriber Stripe fee shrinks as a percentage of revenue at higher price points, so raising your price (or offering an annual plan) reduces your effective fee rate." },
      { q: "Do annual subscriptions have lower effective fees?", a: "Generally yes, because the flat per-transaction Stripe fee is charged once per year instead of monthly, spreading that fixed cost over a much larger transaction amount." },
      { q: "Does Patreon charge subscribers or creators for fees?", a: "Patreon fees are deducted from the creator's payout by default, though some creators opt into a system passing part of the processing fee onto the patron at checkout." },
      { q: "Are these platform fee percentages guaranteed to stay the same?", a: "No, both platforms have changed fee structures before and can adjust rates or introduce new tiers, so always check current published rates before finalizing pricing decisions." },
    ],
    formula: {
      expressions: [
        "Gross = Subs × Price",
        "Stripe_Fee = Gross × 0.029 + Subs × 0.30",
        "Apple_Fee = Gross × iOS% × 0.30",
        "Net = Gross − Platform_Fee − Stripe_Fee − Apple_Fee",
      ],
      variables: [
        { name: "Platform_Fee", meaning: "Percentage cut taken by Substack or Patreon tier" },
        { name: "iOS%", meaning: "Share of subscribers paying via Apple in-app purchase" },
      ],
    },
    related: ["tiktok-live-gift-calculator", "llm-gpu-vram-calculator", "css-clamp-calculator", "cctv-storage-calculator"],
  },

  {
    slug: "llm-gpu-vram-calculator",
    cluster: "digital",
    name: "LLM GPU VRAM Calculator",
    h1: "LLM GPU VRAM Calculator: Model Parameters, Quantization & Context",
    title: "LLM GPU VRAM Calculator | Free Calculator (2026)",
    description:
      "Calculate total GPU VRAM needed instantly. Free online LLM VRAM calculator with live formulas, unit conversion and a step-by-step breakdown.",
    keywords: [
      "llm gpu vram calculator",
      "how much vram to run 70b model",
      "quantization vram requirements calculator",
      "kv cache vram estimate llm",
      "local llm gpu requirements by model size",
    ],
    inputs: [
      {
        id: "params",
        label: "Model parameters",
        type: "select",
        def: "8B",
        options: [
          { value: "7B", label: "7B" },
          { value: "8B", label: "8B" },
          { value: "13B", label: "13B" },
          { value: "34B", label: "34B" },
          { value: "70B", label: "70B" },
        ],
      },
      {
        id: "quant",
        label: "Quantization",
        type: "select",
        def: "q4_k_m",
        options: [
          { value: "fp16", label: "FP16 (16 bits/param)" },
          { value: "q8_0", label: "Q8_0 (8.5 bits/param)" },
          { value: "q5_k_m", label: "Q5_K_M (5.5 bits/param)" },
          { value: "q4_k_m", label: "Q4_K_M (4.5 bits/param)" },
          { value: "q2_k", label: "Q2_K (2.6 bits/param)" },
        ],
      },
      {
        id: "context",
        label: "Context length",
        type: "select",
        def: "8192",
        options: [
          { value: "4096", label: "4k tokens" },
          { value: "8192", label: "8k tokens" },
          { value: "16384", label: "16k tokens" },
          { value: "32768", label: "32k tokens" },
          { value: "65536", label: "64k tokens" },
          { value: "131072", label: "128k tokens" },
        ],
      },
      { id: "batch", label: "Batch size", type: "number", def: "1", unit: "sequences", min: 1, max: 64, step: 1 },
    ],
    compute: (v) => {
      const paramsKey = s(v, "params", "8B");
      const paramsB = LLM_PARAMS[paramsKey] ?? 8;
      const quantKey = s(v, "quant", "q4_k_m");
      const bitsPerParam = QUANT_BITS[quantKey] ?? 4.5;
      const context = Math.max(1, n(v, "context", 8192));
      const batch = Math.max(1, n(v, "batch", 1));

      const weightVramGb = (paramsB * 1e9 * bitsPerParam) / 8 / 1024 ** 3;

      const { layers, kvHeads, headDim } = llmArchConstants(paramsB);
      // KV cache: 2 (K+V) * layers * kvHeads * headDim * context * batch * 2 bytes (fp16)
      const kvCacheBytes = 2 * layers * kvHeads * headDim * context * batch * 2;
      const kvCacheGb = kvCacheBytes / 1024 ** 3;

      const overheadGb = weightVramGb * 0.1; // activation/runtime overhead estimate
      const totalVramGb = weightVramGb + kvCacheGb + overheadGb;
      const gpuRec = recommendGpu(totalVramGb);

      return {
        cards: [
          { label: "Total VRAM needed", value: `${fmt(totalVramGb, 1)} GB`, sub: `${paramsKey} model, ${quantKey.toUpperCase()}, ${fmt(context)} ctx`, accent: true },
          { label: "Model weights", value: `${fmt(weightVramGb, 1)} GB`, sub: `${fmt(bitsPerParam, 1)} bits/param` },
          { label: "KV cache", value: `${fmt(kvCacheGb, 2)} GB`, sub: `${fmt(context)} tokens × batch ${fmt(batch)}` },
          { label: "Recommended GPU", value: gpuRec, sub: `~${fmt(layers)} layers, ${fmt(kvHeads)} KV heads` },
        ],
        table: {
          title: "VRAM breakdown",
          head: ["Component", "Value"],
          rows: [
            ["Parameters", `${paramsKey} (${fmt(paramsB)}B)`],
            ["Quantization", `${quantKey.toUpperCase()} (${fmt(bitsPerParam, 1)} bits/param)`],
            ["Context length", `${fmt(context)} tokens`],
            ["Batch size", fmt(batch)],
            ["Estimated layers", fmt(layers)],
            ["Weight VRAM", `${fmt(weightVramGb, 2)} GB`],
            ["KV cache VRAM", `${fmt(kvCacheGb, 2)} GB`],
            ["Runtime overhead (~10%)", `${fmt(overheadGb, 2)} GB`],
            ["Total VRAM", `${fmt(totalVramGb, 2)} GB`],
          ],
        },
        summary: `A ${paramsKey} model at ${quantKey.toUpperCase()} with ${fmt(context)}-token context needs roughly ${fmt(totalVramGb, 1)}GB of VRAM — a ${gpuRec} should run it comfortably.`,
      };
    },
    intro:
      "Running large language models locally means fitting both the model weights and the growing KV cache attention memory into your GPU's VRAM. This calculator estimates total VRAM demand across common parameter sizes, quantization formats and context lengths, then recommends a GPU tier so you don't buy more (or less) hardware than you need.",
    sections: [
      { h: "Why quantization matters so much", body: "A 7B parameter model at full FP16 precision needs about 14GB just for weights, but the same model quantized to Q4_K_M needs roughly 4GB — a nearly 4x reduction with modest quality loss. This is why almost every locally-run open model in the community is distributed as a quantized GGUF file rather than raw FP16 weights." },
      { h: "The often-overlooked KV cache cost", body: "Every token generated adds a small slice to the key-value cache that attention layers reuse across the sequence, and that cache grows linearly with context length and batch size. At 128k context, KV cache VRAM can rival or exceed the model weights themselves, which is the main reason long-context inference needs far more VRAM than the base model size suggests." },
      { h: "Reading the GPU tier recommendation", body: "Consumer GPUs cluster around 8GB, 12GB, 16GB and 24GB of VRAM, while prosumer and datacenter cards jump to 48GB and 80GB. This tool rounds your estimated requirement up to the next standard tier, leaving some headroom for the OS, other running applications, and inference-engine overhead beyond raw weight and cache math." },
      { h: "Estimating architecture without exact specs", body: "Publicly known layer counts, hidden dimensions and head counts vary between model families even at the same parameter count, so this calculator uses reasonable scaling approximations based on typical transformer architectures. For a model where you know the exact config (from its Hugging Face config.json), treat this tool's output as a close estimate rather than an exact figure." },
    ],
    staticTable: {
      title: "Approximate VRAM by model size and quantization (8k context)",
      head: ["Params", "FP16", "Q8_0", "Q5_K_M", "Q4_K_M", "Q2_K"],
      rows: [
        ["7B", "~14 GB", "~7.4 GB", "~4.8 GB", "~3.9 GB", "~2.3 GB"],
        ["8B", "~16 GB", "~8.5 GB", "~5.5 GB", "~4.5 GB", "~2.6 GB"],
        ["13B", "~26 GB", "~13.8 GB", "~8.9 GB", "~7.3 GB", "~4.2 GB"],
        ["34B", "~68 GB", "~36.1 GB", "~23.4 GB", "~19.1 GB", "~11.0 GB"],
        ["70B", "~140 GB", "~74.4 GB", "~48.1 GB", "~39.4 GB", "~22.8 GB"],
      ],
    },
    faqs: [
      { q: "How much VRAM do I need to run a 70B model?", a: "At Q4_K_M quantization, a 70B model needs roughly 40GB of VRAM for weights alone, plus KV cache and overhead, meaning it typically requires a single 48GB card or two 24GB GPUs in combination." },
      { q: "What quantization level should I use for local inference?", a: "Q4_K_M is the community standard sweet spot, offering strong quality retention with roughly a quarter of the VRAM footprint of FP16; Q5_K_M is a good step up if you have spare VRAM headroom." },
      { q: "Why does context length affect VRAM so much?", a: "The KV cache that stores attention keys and values for every previous token grows linearly with context length, so doubling your context window roughly doubles that portion of VRAM usage." },
      { q: "Can I run a 13B model on a 12GB GPU?", a: "At Q4_K_M quantization yes, since weights need roughly 7.3GB, leaving enough headroom for a moderate context window and runtime overhead on a 12GB card." },
      { q: "Does batch size matter for single-user chat use?", a: "For a single interactive chat session batch size is effectively 1; it mainly matters for serving multiple concurrent users or running parallel generation requests." },
      { q: "Is FP16 ever worth the extra VRAM cost?", a: "FP16 preserves the most model quality and is preferred for fine-tuning or benchmarking research, but for everyday local inference, quantized formats like Q4_K_M or Q5_K_M offer a much better VRAM-to-quality tradeoff." },
      { q: "Why do two different 7B models need different amounts of VRAM?", a: "Architecture details like hidden dimension, layer count, and vocabulary size vary between model families even at the same parameter count, causing modest differences in real-world VRAM needs beyond this estimate." },
      { q: "What GPU should I buy for running 8B models comfortably?", a: "A 12GB or 16GB consumer GPU comfortably runs 8B models at Q4 to Q5 quantization with moderate context length, leaving room to experiment with longer contexts or slightly larger models later." },
      { q: "Does CPU offloading reduce the VRAM requirement?", a: "Yes, tools like llama.cpp can offload some model layers to system RAM and CPU, trading inference speed for lower VRAM usage, which is useful when your GPU falls just short of the required amount." },
      { q: "How accurate is this VRAM estimate compared to real-world usage?", a: "It's a solid planning estimate within roughly 10-20%, but actual usage varies by inference engine, CUDA/kernel overhead, and whether flash-attention or paged KV cache optimizations are enabled." },
    ],
    formula: {
      expressions: [
        "Weight_VRAM = (Params × Bits_per_param) / 8 / 1024³",
        "KV_Cache = 2 × layers × kv_heads × head_dim × context × batch × 2 bytes",
        "Total_VRAM = Weight_VRAM + KV_Cache + 10% overhead",
      ],
      variables: [
        { name: "Bits_per_param", meaning: "Quantization precision in bits per parameter" },
        { name: "kv_heads", meaning: "Number of key-value attention heads (architecture-dependent)" },
        { name: "head_dim", meaning: "Dimension per attention head, typically 128" },
      ],
    },
    related: ["css-clamp-calculator", "cctv-storage-calculator", "podcast-mic-gain-calculator", "tiktok-live-gift-calculator"],
  },

  {
    slug: "cctv-storage-calculator",
    cluster: "digital",
    name: "CCTV IP Camera Storage Calculator",
    h1: "CCTV IP Camera Storage Capacity & Bandwidth Days Calculator",
    title: "CCTV Storage Calculator | Free Calculator (2026)",
    description:
      "Calculate total storage needed instantly. Free online CCTV storage calculator with live formulas, unit conversion and a step-by-step breakdown.",
    keywords: [
      "cctv storage calculator",
      "how much hard drive space for ip cameras",
      "nvr storage calculator by camera count",
      "h265 vs h264 storage savings calculator",
      "cctv retention days hdd size calculator",
    ],
    inputs: [
      { id: "cameraCount", label: "Number of cameras", type: "number", def: "8", unit: "cameras", min: 1, max: 256, step: 1 },
      {
        id: "resolution",
        label: "Resolution",
        type: "select",
        def: "4K",
        options: [
          { value: "1080p", label: "1080p (Full HD)" },
          { value: "2K", label: "2K (QHD)" },
          { value: "4K", label: "4K (UHD)" },
        ],
      },
      {
        id: "compression",
        label: "Compression codec",
        type: "select",
        def: "h265",
        options: [
          { value: "h264", label: "H.264 (baseline)" },
          { value: "h265", label: "H.265 / HEVC" },
          { value: "h265+", label: "H.265+ (smart codec)" },
        ],
      },
      { id: "fps", label: "Frame rate", type: "number", def: "15", unit: "fps", min: 1, max: 60, step: 1 },
      {
        id: "schedule",
        label: "Recording schedule",
        type: "select",
        def: "24_7",
        options: [
          { value: "24_7", label: "24/7 continuous (24h/day)" },
          { value: "motion", label: "Motion-triggered only (~8h/day)" },
        ],
      },
      { id: "retention", label: "Retention period", type: "number", def: "30", unit: "days", min: 1, max: 365, step: 1 },
    ],
    compute: (v) => {
      const cameraCount = Math.max(1, n(v, "cameraCount", 8));
      const resolution = s(v, "resolution", "4K");
      const baseBitrateMbps = RES_BITRATE[resolution] ?? 12;
      const compression = s(v, "compression", "h265");
      const compFactor = COMPRESSION_FACTOR[compression] ?? 0.5;
      const fps = Math.max(1, n(v, "fps", 15));
      const fpsFactor = fps / 15; // baseline bitrate assumed at 15fps
      const schedule = s(v, "schedule", "24_7");
      const hoursPerDay = schedule === "24_7" ? 24 : 8;
      const retention = Math.max(1, n(v, "retention", 30));

      const bitratePerCameraMbps = baseBitrateMbps * compFactor * fpsFactor;
      const dailyGbPerCamera = (bitratePerCameraMbps * hoursPerDay * 3600) / 8 / 1024;
      const dailyGbTotal = dailyGbPerCamera * cameraCount;
      const totalTb = (dailyGbTotal * retention) / 1024;
      const hddRec = recommendHdd(totalTb);

      return {
        cards: [
          { label: "Total storage needed", value: `${fmt(totalTb, 2)} TB`, sub: `${fmt(cameraCount)} cameras × ${fmt(retention)} days`, accent: true },
          { label: "Bitrate per camera", value: `${fmt(bitratePerCameraMbps, 2)} Mbps`, sub: `${resolution}, ${compression.toUpperCase()}, ${fmt(fps)}fps` },
          { label: "Daily storage (all cameras)", value: `${fmt(dailyGbTotal, 1)} GB`, sub: `${fmt(hoursPerDay)}h/day recording` },
          { label: "Recommended drive", value: hddRec, sub: `For ${fmt(retention)}-day retention` },
        ],
        table: {
          title: "Storage breakdown",
          head: ["Metric", "Value"],
          rows: [
            ["Cameras", fmt(cameraCount)],
            ["Resolution", resolution],
            ["Codec", compression.toUpperCase()],
            ["Frame rate", `${fmt(fps)} fps`],
            ["Bitrate per camera", `${fmt(bitratePerCameraMbps, 2)} Mbps`],
            ["Recording hours/day", `${fmt(hoursPerDay)}h`],
            ["Daily GB per camera", `${fmt(dailyGbPerCamera, 2)} GB`],
            ["Daily GB (all cameras)", `${fmt(dailyGbTotal, 1)} GB`],
            [`Total for ${fmt(retention)} days`, `${fmt(totalTb, 2)} TB`],
          ],
        },
        summary: `${fmt(cameraCount)} ${resolution} cameras recording ${schedule === "24_7" ? "24/7" : "on motion (~8h/day)"} with ${compression.toUpperCase()} need about ${fmt(totalTb, 2)}TB for ${fmt(retention)} days — a ${hddRec} covers it.`,
      };
    },
    intro:
      "Sizing an NVR or DVR hard drive correctly means balancing camera resolution, compression codec, frame rate, recording schedule and how many days of footage you legally or practically need to retain. This calculator runs those numbers together so you can buy the right drive the first time instead of running out of storage mid-month.",
    sections: [
      { h: "Why codec choice changes storage by half or more", body: "H.264 has been the baseline video codec for over a decade, but H.265 (HEVC) compresses the same visual quality into roughly half the bitrate, and smart codecs like H.265+ add scene-change detection to cut it further during static periods. Upgrading from H.264 to H.265+ on an 8-camera system can turn a 4TB requirement into under 1.5TB for the same retention window." },
      { h: "Continuous vs motion-triggered recording", body: "Recording 24/7 gives complete footage but at roughly three times the storage cost of motion-triggered recording, which typically captures 6-10 active hours per day in a residential or small business setting. High-traffic commercial sites usually need continuous recording for liability reasons, while quieter properties can save significant storage with motion detection alone." },
      { h: "How resolution scales storage demand", body: "Moving from 1080p to 4K roughly triples the raw bitrate need before compression, since 4K carries four times the pixel count of 1080p. For most security use cases, 2K offers a solid middle ground — enough detail for facial recognition and license plates at reasonable distances without 4K's storage and bandwidth burden." },
      { h: "Choosing your retention period", body: "Many jurisdictions and insurance policies require a minimum 30-day retention for commercial CCTV footage, though some regulated industries (banking, cannabis retail) mandate 90 days or longer. Check local regulations before finalizing your drive size, since retrofit expansion later usually costs more than buying adequate capacity upfront." },
    ],
    staticTable: {
      title: "Approximate daily storage per camera (24/7, H.265, 15fps)",
      head: ["Resolution", "Bitrate (Mbps)", "Daily storage/camera", "30-day storage/camera"],
      rows: [
        ["1080p", "2.0", "~21 GB", "~630 GB"],
        ["2K", "3.0", "~31 GB", "~950 GB"],
        ["4K", "6.0", "~63 GB", "~1.9 TB"],
      ],
    },
    faqs: [
      { q: "How much storage do I need for 8 cameras at 4K for 30 days?", a: "With H.265 compression at 15fps recording 24/7, expect roughly 15TB for 8 cameras over 30 days; switching to motion-only recording can cut that to around 5TB." },
      { q: "Does H.265+ really save that much storage over H.264?", a: "Yes, H.265+ combines HEVC's baseline efficiency with smart scene-change encoding, often achieving 60-70% storage savings over H.264 in typical scenes with limited motion." },
      { q: "Should I record 24/7 or only on motion detection?", a: "24/7 recording guarantees you never miss an event but costs roughly three times the storage of motion-triggered recording; many commercial sites use 24/7 for liability while residential setups often use motion detection to save cost." },
      { q: "How many days of CCTV footage am I legally required to keep?", a: "Requirements vary widely by jurisdiction and industry — many places default to 30 days for general commercial premises, but banks, casinos, and some regulated retail sectors require 90 days or more, so check local law." },
      { q: "What size hard drive should I buy for a home security system?", a: "For a typical 4-camera 1080p home setup with motion-triggered recording and 30-day retention, a 1-2TB drive is usually sufficient; add more headroom if you're recording continuously or using higher resolution." },
      { q: "Does frame rate significantly affect storage size?", a: "Yes, storage scales roughly linearly with frame rate, so dropping from 30fps to 15fps cuts storage needs nearly in half with minimal impact on typical security footage usability." },
      { q: "Is 4K resolution overkill for most security cameras?", a: "For general area monitoring, 2K or even 1080p is often sufficient, but 4K is valuable at entry points and parking areas where you need to read license plates or identify faces at distance." },
      { q: "What's the difference between NVR and DVR storage needs?", a: "NVRs process IP camera streams with more flexible, often higher, resolution and bitrate options, while DVRs handle analog cameras at generally lower resolutions — but the storage math (bitrate × time) works the same way for both." },
      { q: "Should I use RAID for CCTV storage redundancy?", a: "For business-critical surveillance, yes — a RAID 1 or RAID 5 array protects against drive failure wiping out your entire footage archive, at the cost of some usable capacity for redundancy." },
      { q: "How do I reduce CCTV storage costs without losing coverage?", a: "Combine H.265+ compression, motion-triggered recording during low-traffic hours, and a lower resolution on wide-area cameras while reserving higher resolution for critical entry points — this layered approach often cuts storage needs by more than half." },
    ],
    formula: {
      expressions: [
        "Bitrate_per_cam = Base_Bitrate × Compression_Factor × (fps/15)",
        "Daily_GB = (Bitrate_Mbps × hours × 3600) / 8 / 1024 × cameras",
        "Total_TB = (Daily_GB × retention_days) / 1024",
      ],
      variables: [
        { name: "Base_Bitrate", meaning: "Baseline Mbps for resolution tier at 15fps" },
        { name: "Compression_Factor", meaning: "Relative bitrate multiplier for codec (H.264=1.0, H.265=0.5, H.265+=0.35)" },
        { name: "hours", meaning: "Recording hours per day (24 for continuous, 8 for motion-triggered)" },
      ],
    },
    related: ["llm-gpu-vram-calculator", "podcast-mic-gain-calculator", "css-clamp-calculator", "substack-patreon-fee-calculator"],
  },
];
